// Attention engine — surfaces STRUCTURAL scheduling issues that need a planner's
// decision: double-booked presses (overlap) and lanes that spill past the 20:00
// shift end (overbooked). ETA-vs-plan "late" (finishes-after-ship-time) alerting
// was intentionally removed — the board tracks what's running / how far through /
// what's next, not deadline risk.
//
// PARKED: both remaining checks are gated behind ATTENTION_CHECKS_ENABLED, which
// is currently false — with it off analyze() returns an empty analysis and the
// panel holds its space with a placeholder. The detection below is kept intact
// (and still under test) as the starting point for a smarter engine.
import { ATTENTION_CHECKS_ENABLED, SHIFT_END_MIN, PARK_CODE } from './ganttConfig';

export type IssueKind = 'overlap' | 'passweld';

export interface AnalyzeEntry {
  id: number | string;
  press: string;
  absStart: number; // minutes from today 00:00
  absEnd: number;
  day: number;      // day offset (0 = today)
  status?: string | null;
  due?: number | null; // due day offset
  job?: string;
  cust?: string;
  desc?: string;
  pass?: number;    // press pass (1 = front, 2 = back); absent = single-pass job
}

export interface Issue {
  id: number | string;
  kind: IssueKind;
  job?: string;
  press: string;
  cust?: string;
  desc?: string;
  day: number;
  absStart: number;
  label: string;
}

export interface LaneOver {
  spillMin: number;
  latest: number;
}

export interface Analysis {
  flags: Record<string, IssueKind[]>;
  issues: Issue[];
  laneOver: Record<string, LaneOver>;
  counts: { overlap: number; overbooked: number; passweld: number };
}

/** Nothing to see — the shape the board consumes when checks are off. */
export function emptyAnalysis(): Analysis {
  return { flags: {}, issues: [], laneOver: {}, counts: { overlap: 0, overbooked: 0, passweld: 0 } };
}

/** What the board calls. Gated; see the ATTENTION_CHECKS_ENABLED note above.
 *
 * The gate is PER-CHECK, not all-or-nothing. It was switched off because overlap
 * and overbooked fired on things a planner can't act on — that reasoning is
 * about those two rules, not about the panel. `passweld` is the case the comment
 * on the flag anticipates: it fires rarely, it means something specific, and the
 * planner can fix it by moving a bar. So it reports even with the gate down,
 * and the noisy pair stays suppressed until someone wants them back.
 */
export function analyze(entries: AnalyzeEntry[]): Analysis {
  const full = runChecks(entries);
  if (ATTENTION_CHECKS_ENABLED) return full;

  const issues = full.issues.filter((i) => i.kind === 'passweld');
  const flags: Record<string, IssueKind[]> = {};
  issues.forEach((i) => {
    const key = String(i.id);
    flags[key] = flags[key] || [];
    if (!flags[key].includes(i.kind)) flags[key].push(i.kind);
  });
  return {
    flags,
    issues,
    laneOver: {},   // overbooked is one of the suppressed rules
    counts: { overlap: 0, overbooked: 0, passweld: issues.length },
  };
}

/** The checks themselves, kept exercisable independently of the gate. */
export function runChecks(entries: AnalyzeEntry[]): Analysis {
  const flags: Record<string, IssueKind[]> = {};
  const add = (id: number | string, k: IssueKind) => {
    const key = String(id);
    flags[key] = flags[key] || [];
    if (!flags[key].includes(k)) flags[key].push(k);
  };

  const byLane: Record<string, AnalyzeEntry[]> = {};
  entries.forEach((e) => {
    if (e.press !== PARK_CODE) (byLane[e.press] = byLane[e.press] || []).push(e);
  });

  // overlaps — two runs sharing a press at the same time
  Object.values(byLane).forEach((list) => {
    const s = [...list].sort((a, b) => a.absStart - b.absStart);
    for (let i = 0; i < s.length; i++) {
      for (let j = i + 1; j < s.length; j++) {
        if (s[j].absStart < s[i].absEnd && s[i].absStart < s[j].absEnd) {
          add(s[i].id, 'overlap');
          add(s[j].id, 'overlap');
        }
      }
    }
  });

  // pass weld — the two passes of one job sitting BACK-TO-BACK on a lane with
  // nothing between them. Prinect reports a job number but not a pass, so in
  // that arrangement the actuals sync cannot tell pass 2 starting from pass 1
  // still running: pass 1 never closes, and pass 2's live progress lands on the
  // pass 1 bar (see the resolver note in blueprints/production.py). Any other
  // job in between gives the sync the transition it needs, which is why the
  // shop's usual "all the fronts, then all the backs" order is safe.
  // Detected at PLAN time because that's when it's cheap to fix — drop anything
  // between the two passes, or accept it knowing pass 2 won't track live.
  // Only over work that hasn't finished. This is a PLANNING warning — "move one
  // of these before it runs" — and there is nothing to move once the sheets are
  // through. Historical pairs would otherwise sit in the panel permanently,
  // which is how a useful check turns into wallpaper. It matters in practice:
  // passes recorded retrospectively land adjacent on the lane by construction.
  Object.values(byLane).forEach((list) => {
    const s = list
      .filter((e) => e.status !== 'completed' && e.status !== 'cancelled')
      .sort((a, b) => a.absStart - b.absStart);
    for (let i = 0; i + 1 < s.length; i++) {
      const a = s[i];
      const b = s[i + 1];
      if (a.job && a.job === b.job && (a.pass ?? 1) !== (b.pass ?? 1)) {
        add(a.id, 'passweld');
        add(b.id, 'passweld');
      }
    }
  });

  // overbooked lanes — today's work runs past the 20:00 shift end
  const laneOver: Record<string, LaneOver> = {};
  Object.entries(byLane).forEach(([p, list]) => {
    const latest = Math.max(0, ...list.filter((e) => e.day === 0).map((e) => e.absEnd));
    if (latest > SHIFT_END_MIN) laneOver[p] = { spillMin: latest - SHIFT_END_MIN, latest };
  });

  // sorted issue list
  const SEV: Record<IssueKind, number> = { overlap: 0, passweld: 1 };
  const LABEL: Record<IssueKind, string> = {
    overlap: 'Press double-booked',
    passweld: 'Both passes back-to-back — pass 2 won’t track live',
  };
  const issues: Issue[] = [];
  entries.forEach((e) => {
    const fl = flags[String(e.id)];
    if (!fl) return;
    fl.forEach((k) =>
      issues.push({
        id: e.id, kind: k, job: e.job, press: e.press, cust: e.cust,
        desc: e.desc, day: e.day, absStart: e.absStart, label: LABEL[k],
      }),
    );
  });
  issues.sort((a, b) => SEV[a.kind] - SEV[b.kind] || a.absStart - b.absStart);

  return {
    flags,
    issues,
    laneOver,
    counts: {
      overlap: issues.filter((i) => i.kind === 'overlap').length,
      overbooked: Object.keys(laneOver).length,
      passweld: issues.filter((i) => i.kind === 'passweld').length,
    },
  };
}
